import {
  PersonStatusTier,
  type PlayerProfileResponse,
  type PublicMeasurementAveragesResponseRecordMeasurementComparisonRelatedResponseRelatedPagedData,
  type PublicMeasurementResponseRecord,
} from '@on3/api';
import { Check } from '@on3/ui-lib/components/Svg';
import { useAuth } from '@on3/ui-lib/index';
import { slugify } from '@on3/ui-lib/utils/slugify';
import clsx from 'clsx';
import { PremiumBlock } from 'components/Db/Profile/PremiumBlock/PremiumBlock';
import useAthlete from 'contexts/AthleteProvider';
import Link from 'next/link';
import { useCallback, useMemo } from 'react';
import { createTitle } from 'utils/meta';

import { VitalsMock } from './Mock';
import styles from './ProfileVitals.module.scss';

export interface ProfileVitalsResponse {
  playerMeasurements: PublicMeasurementResponseRecord[];
}

interface ProfileVitalsProps {
  player: PlayerProfileResponse;
  measurements?: PublicMeasurementResponseRecord[] | null;
  averages?: PublicMeasurementAveragesResponseRecordMeasurementComparisonRelatedResponseRelatedPagedData | null;
}

const Measurement = ({ data }: { data: PublicMeasurementResponseRecord }) => {
  const formatValue = useCallback(
    (value?: number | null) => {
      if (!value && value !== 0) return '-';

      // Height
      if (data.measurementTypeKey === 1) {
        return `${Math.floor(value / 12)}-${(value % 12).toFixed(0)}`;
      }

      // Weight
      if (data.measurementTypeKey === 2) return Math.round(value);

      // 40 Yard Dash
      if (data.measurementTypeKey === 6) {
        return value.toFixed(2);
      }

      return value.toFixed(1);
    },
    [data.measurementTypeKey],
  );

  const formatAverage = (average?: number | null) => {
    if (!average) return '-';

    return average.toFixed(1);
  };

  const formatHeight = useCallback(
    (value?: number | null) => {
      const topValue = Math.max(
        data.value ?? 0,
        data.top300Average ?? 0,
        data.draftedAverage ?? 0,
      );

      return { height: `calc(${(value ?? 0) / topValue} * 150px)` };
    },
    [data],
  );

  return (
    <div
      aria-labelledby={`graph-${data.type?.toLowerCase()}`}
      className={styles.measurement}
    >
      <h3 id={`graph-${data.type?.toLowerCase()}`}>{data.type}</h3>
      <div className={styles.verified}>
        {data.verified && (
          <>
            <figure>
              <Check fill="#fff" />
            </figure>
            <span>On3 Verified</span>
          </>
        )}
      </div>
      <div className={styles.graph}>
        <div
          className={styles.bar}
          data-player
          style={formatHeight(data.value)}
        >
          <span>{formatValue(data.value)}</span>
        </div>
        <div
          className={styles.bar}
          data-top300
          style={formatHeight(data.top300Average)}
        >
          <span>{formatValue(data.top300Average)}</span>
        </div>
        <div
          className={styles.bar}
          data-drafted
          style={formatHeight(data.draftedAverage)}
        >
          <span>{formatValue(data.draftedAverage)}</span>
        </div>
      </div>
      <div className={styles.averages}>
        <span
          className={clsx({
            [styles.positive]: (data.topAverageChangePercent ?? 0) >= 100,
            [styles.negative]: (data.topAverageChangePercent ?? 0) < 100,
          })}
        >
          {`${formatAverage(data.topAverageChangePercent)}%`}
        </span>
        <span
          className={clsx({
            [styles.positive]: (data.draftChangePercent ?? 0) >= 100,
            [styles.negative]: (data.draftChangePercent ?? 100) < 100,
          })}
        >
          {`${formatAverage(data.draftChangePercent)}%`}
        </span>
      </div>
    </div>
  );
};

export const ProfileVitals = ({
  player,
  measurements,
  averages,
}: ProfileVitalsProps) => {
  const { user } = useAuth();
  const { hasBaseProfile } = useAthlete();
  const showMockVitals = hasBaseProfile && !measurements?.length;
  const showVitals = (user?.has || hasBaseProfile) && !!measurements?.length;
  const source = slugify(player.name || 'athlete');

  const sportOrg = useMemo(() => {
    if (player.defaultSport?.key === 1) return 'NFL Combine';
    if (player.defaultSport?.key === 2) return 'NBA Combine';

    return null;
  }, [player.defaultSport]);
  const numYears = averages?.relatedModel?.numberOfYears ?? 5;

  if (showMockVitals) {
    return <VitalsMock />;
  }

  return (
    <div className={styles.block}>
      <figure aria-labelledby="vitals-legend" className={styles.vitals}>
        <figcaption id="vitals-legend">
          <ul>
            <li>
              <strong>{player.name}</strong>
              <Link href={`/high-school/${player.highSchool?.urlSlug}/`}>
                {player.highSchoolName}
              </Link>
            </li>
            <li>
              <strong>Top 300 {player.positionAbbreviation} Avg</strong>
              <span>Avg. Last {numYears} Years</span>
            </li>
            <li>
              <strong>{createTitle(sportOrg, 'Drafted')}</strong>
              <span>Avg. Last {numYears} Years</span>
            </li>
          </ul>
        </figcaption>

        {showVitals ? (
          <div className={styles.graphs}>
            {measurements?.map((measurement) => (
              <Measurement data={measurement} key={measurement.key} />
            ))}
          </div>
        ) : (
          <div className={styles.premium}>
            <PremiumBlock
              utmCampaign="player_vitals"
              utmContent="subscribe_link"
              utmMedium="web"
              utmSource={source}
            />
          </div>
        )}
      </figure>
    </div>
  );
};
